import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export function detectPlatform() {
  if (typeof window === "undefined") return "other";
  const ua = window.navigator.userAgent || "";
  const platform = window.navigator.platform || "";
  if (/iPad|iPhone|iPod/.test(ua) || (platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Win/i.test(platform)) return "windows";
  if (/Mac/i.test(platform)) return "mac";
  return "other";
}

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function InstructionsForPlatform({ platform }) {
  const bullets = {
    ios: [
      { icon: "↑", text: "Tap the Share icon (the square with the arrow pointing up) at the bottom of Safari." },
      { icon: "+", text: "Scroll down and choose Add to Home Screen." },
      { icon: "✓", text: "Tap Add. Business Showers will appear on your home screen." },
    ],
    android: [
      { icon: "⋮", text: "Open your browser menu (the three-dot icon in the top right)." },
      { icon: "+", text: "Choose Install app or Add to Home Screen." },
      { icon: "✓", text: "Confirm. Business Showers will install like an app." },
    ],
    windows: [
      { icon: "⌨", text: "Press Ctrl + D to bookmark this page." },
      { icon: "★", text: "Save it to your Bookmarks Bar so it's one click away every time." },
    ],
    mac: [
      { icon: "⌨", text: "Press ⌘ Command + D to bookmark this page." },
      { icon: "★", text: "Save it to your Favorites Bar so it's one click away every time." },
    ],
    other: [
      { icon: "⌨", text: "Bookmark this page in your browser (Ctrl + D or ⌘ D)." },
      { icon: "★", text: "Save it to your Favorites for quick access." },
    ],
  }[platform || "other"];

  const label = {
    ios: "On your iPhone or iPad",
    android: "On your Android device",
    windows: "On your Windows computer",
    mac: "On your Mac",
    other: "On your device",
  }[platform || "other"];

  return (
    <div data-testid={`pwa-instructions-${platform}`} className="space-y-3">
      <div className="text-[11px] uppercase tracking-[0.25em] text-rose-600 font-[Outfit] font-bold">{label}</div>
      <ol className="space-y-3">
        {bullets.map((b, i) => (
          <li key={i} className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="font-[Cormorant_Garamond] text-2xl text-emerald-800 leading-none w-8 text-center">{b.icon}</div>
            <div className="font-[Outfit] text-sm text-slate-800 leading-relaxed">{b.text}</div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function useInstallPrompt() {
  const [available, setAvailable] = useState(Boolean(typeof window !== "undefined" && window.__bsInstallPrompt));
  useEffect(() => {
    const onAvail = () => setAvailable(true);
    const onDone = () => setAvailable(false);
    window.addEventListener("bs-install-available", onAvail);
    window.addEventListener("bs-install-done", onDone);
    return () => {
      window.removeEventListener("bs-install-available", onAvail);
      window.removeEventListener("bs-install-done", onDone);
    };
  }, []);

  const triggerInstall = async () => {
    const p = window.__bsInstallPrompt;
    if (!p) return { ok: false, reason: "unavailable" };
    try {
      await p.prompt();
      const choice = await p.userChoice;
      window.__bsInstallPrompt = null;
      setAvailable(false);
      return { ok: true, outcome: choice?.outcome };
    } catch (err) {
      return { ok: false, reason: "error", err };
    }
  };

  return { available, triggerInstall };
}

export function InstallInstructionsModal({ open, onOpenChange, platform }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pwa-instructions-modal" className="max-w-md bg-[#FFFAF0]">
        <DialogHeader>
          <div className="text-[11px] uppercase tracking-[0.25em] text-emerald-700 font-[Outfit] font-bold">
            Save This Page
          </div>
          <DialogTitle className="font-[Cormorant_Garamond] text-2xl text-emerald-950 leading-tight">
            Save the Official Business Showers Event Page
          </DialogTitle>
          <DialogDescription className="font-[Outfit] text-slate-600">
            Pick the steps for your device below — takes about 10 seconds.
          </DialogDescription>
        </DialogHeader>
        <InstructionsForPlatform platform={platform} />
      </DialogContent>
    </Dialog>
  );
}

export default function SavePageHero() {
  const { available, triggerInstall } = useInstallPrompt();
  const [showInstr, setShowInstr] = useState(false);
  const [platform] = useState(() => detectPlatform());

  if (isStandalone()) return null;

  const handleClick = async () => {
    if (available) {
      const r = await triggerInstall();
      if (r.ok) return;
    }
    setShowInstr(true);
  };

  return (
    <section
      data-testid="save-page-cta"
      className="bg-amber-50 border-y-2 border-amber-300"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-6 sm:py-8">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
          <div className="flex-shrink-0">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white border border-emerald-300 flex items-center justify-center shadow-md overflow-hidden">
              <img src="/bs-logo-square.png" alt="Business Showers" className="w-full h-full object-contain p-1.5" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <Button
              data-testid="save-page-button"
              onClick={handleClick}
              className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white font-[Outfit] font-semibold h-12 px-6 text-base shadow-md"
            >
              Save the Official Business Showers Event Page
            </Button>
            <p className="font-[Outfit] text-sm text-slate-700 mt-3 leading-relaxed max-w-3xl">
              Save this page for quick access to live pitches, community voting, finalist and honoree announcements,
              tickets, sponsorship and volunteer opportunities, event details, and the complete 2026 Business Showers experience.
            </p>
          </div>
        </div>
      </div>
      <InstallInstructionsModal open={showInstr} onOpenChange={setShowInstr} platform={platform} />
    </section>
  );
}
